import React, { useEffect, useState } from "react";
import { UserPlus, X, Compass, HeartHandshake, Users, Crown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "dados", label: "Dados", icon: Compass },
  { key: "familia", label: "Família", icon: HeartHandshake },
  { key: "ritual", label: "Datas Ritualísticas", icon: Users },
  { key: "graus", label: "Graus 4-33", icon: Crown },
];

// Normaliza cargo legado para o valor exato do dropdown (evita "venerável" ≠ "Venerável Mestre")
const OFFICE_ALIASES = {
  "veneravel": "Venerável Mestre",
  "veneravel mestre": "Venerável Mestre",
  "venerável": "Venerável Mestre",
  "1o vigilante": "1º Vigilante",
  "primeiro vigilante": "1º Vigilante",
  "2o vigilante": "2º Vigilante",
  "segundo vigilante": "2º Vigilante",
  "secretario": "Secretário",
  "chancelaria": "Chanceler",
  "tesouraria": "Tesoureiro",
  "hospitaleiro": "Hospitaleiro",
  "mestre de cerimonias": "Mestre de Cerimônias",
  "mestre instalado": "Mestre Instalado (Past-Venerável)",
  "past-veneravel": "Mestre Instalado (Past-Venerável)",
  "past veneravel": "Mestre Instalado (Past-Venerável)",
};

const normalizeOffice = (raw) => {
  if (!raw) return "Membro do Quadro";
  const key = String(raw).trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return OFFICE_ALIASES[key] || raw;
};

export const MemberModal = ({ isOpen, onClose, onSubmit, initialData = null, isEditing = false, defaultMonthlyFee = 180 }) => {
  const [tab, setTab] = useState("dados");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: "",
    cim: "",
    degree: "1 - Aprendiz",
    office: "Membro do Quadro",
    email: "",
    phone: "",
    initiation_date: "",
    elevation_date: "",
    exaltation_date: "",
    installation_date: "",
    emerito_date: "",
    provecto_date: "",
    cancellation_request_date: "",
    regularization_date: "",
    monthly_fee: defaultMonthlyFee,
    status: "regular",
    notes: "",
    birthday: "",
    spouse_name: "",
    spouse_birthday: "",
    wedding_date: "",
    children: [],
    grandchildren: [],
    higher_degrees: [],
    photo_b64: null,
    honor_title: "",
    honor_title_granted_at: "",
    password: "",
    password_confirm: "",
  });

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: initialData?.name || "",
        cim: initialData?.cim || "",
        degree: initialData?.degree || "1 - Aprendiz",
        office: normalizeOffice(initialData?.office),
        email: initialData?.email || "",
        phone: initialData?.phone || "",
        initiation_date: initialData?.initiation_date || "",
        elevation_date: initialData?.elevation_date || "",
        exaltation_date: initialData?.exaltation_date || "",
        installation_date: initialData?.installation_date || "",
        emerito_date: initialData?.emerito_date || "",
        provecto_date: initialData?.provecto_date || "",
        cancellation_request_date: initialData?.cancellation_request_date || "",
        regularization_date: initialData?.regularization_date || "",
        monthly_fee: initialData?.monthly_fee ?? defaultMonthlyFee,
        status: initialData?.status || "regular",
        notes: initialData?.notes || "",
        birthday: initialData?.birthday || "",
        spouse_name: initialData?.spouse_name || "",
        spouse_birthday: initialData?.spouse_birthday || "",
        wedding_date: initialData?.wedding_date || "",
        children: initialData?.children || [],
        grandchildren: initialData?.grandchildren || [],
        higher_degrees: initialData?.higher_degrees || [],
        photo_b64: initialData?.photo_b64 || null,
        honor_title: initialData?.honor_title || "",
        honor_title_granted_at: initialData?.honor_title_granted_at || "",
        password: "",
        password_confirm: "",
      });
      setTab("dados");
    }
  }, [isOpen, initialData, defaultMonthlyFee]);

  // Auto-seleciona o grau simbólico com base nas datas ritualísticas (Instalação > Exaltação > Elevação > Iniciação)
  useEffect(() => {
    if (!isOpen) return;
    let auto = null;
    if (form.installation_date) auto = "Mestre Instalado";
    else if (form.exaltation_date) auto = "3 - Mestre";
    else if (form.elevation_date) auto = "2 - Companheiro";
    else if (form.initiation_date) auto = "1 - Aprendiz";
    if (auto && auto !== form.degree) {
      setForm((prev) => ({ ...prev, degree: auto }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.initiation_date, form.elevation_date, form.exaltation_date, form.installation_date, isOpen]);

  if (!isOpen) return null;

  const setField = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const addChild = () => setField({ children: [...form.children, { name: "", birthday: "", relation: "filho(a)" }] });
  const rmChild = (i) => setField({ children: form.children.filter((_, idx) => idx !== i) });
  const updChild = (i, patch) =>
    setField({ children: form.children.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  const addGrand = () => setField({ grandchildren: [...form.grandchildren, { name: "", birthday: "", relation: "neto(a)" }] });
  const rmGrand = (i) => setField({ grandchildren: form.grandchildren.filter((_, idx) => idx !== i) });
  const updGrand = (i, patch) =>
    setField({ grandchildren: form.grandchildren.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  const addDegree = () => setField({ higher_degrees: [...form.higher_degrees, { degree: 4, date: "", obs: "" }] });
  const rmDegree = (i) => setField({ higher_degrees: form.higher_degrees.filter((_, idx) => idx !== i) });
  const updDegree = (i, patch) =>
    setField({ higher_degrees: form.higher_degrees.map((d, idx) => (idx === i ? { ...d, ...patch } : d)) });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Informe o nome do Irmão.");
      setTab("dados");
      return;
    }
    if (!form.cim.trim()) {
      toast.error("Informe o CIM (Cadastro Maçônico).");
      setTab("dados");
      return;
    }
    // Se digitou senha, precisa confirmar corretamente
    if (form.password && form.password.trim() !== (form.password_confirm || "").trim()) {
      toast.error("As senhas digitadas não coincidem. Confira antes de salvar.");
      setTab("dados");
      return;
    }
    const payload = {
      ...form,
      children: (form.children || []).filter((c) => c.name?.trim()),
      grandchildren: (form.grandchildren || []).filter((c) => c.name?.trim()),
      higher_degrees: (form.higher_degrees || []).filter((d) => d.degree),
      monthly_fee: parseFloat(form.monthly_fee) || 180,
    };
    // Remove o campo de confirmação — não vai para o backend
    delete payload.password_confirm;
    // Se estiver editando e senha vazia → remove do payload para NÃO sobrescrever
    if (isEditing && !form.password?.trim()) {
      delete payload.password;
    }

    // Regra Ouro: se o Master está PROMOVENDO o Irmão (adicionou data de Elevação ou
    // Exaltação nova) e o Irmão NÃO estava APTO pela Regra Ouro do grau anterior,
    // pede uma confirmação — o Venerável mantém a autonomia final.
    if (isEditing && initialData?.frequency_rule && initialData.frequency_rule.apto === false) {
      const wasPromoted =
        (payload.elevation_date && !initialData.elevation_date && initialData.degree === "1 - Aprendiz") ||
        (payload.exaltation_date && !initialData.exaltation_date && initialData.degree === "2 - Companheiro");
      if (wasPromoted) {
        const rule = initialData.frequency_rule;
        const proceed = window.confirm(
          `⚠️ Regra Ouro de Frequência NÃO atendida\n\n` +
          `${initialData.name} tem apenas ${rule.attended}/${rule.sessions_since || 0} presenças ` +
          `(${rule.percent}%) desde a ${rule.anchor_label} — mínimo exigido é ${rule.min_percent}%.\n\n` +
          `${rule.msg}\n\n` +
          `Deseja promover MESMO ASSIM? (registrado na trilha de auditoria)`
        );
        if (!proceed) {
          toast.info("Promoção cancelada. Aguarde as presenças necessárias.");
          return;
        }
      }
    }

    onSubmit(payload);
  };

  const cls = "w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37] text-sm outline-none";
  const lbl = "block mb-1 font-semibold text-slate-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div
        data-testid="modal-member-form"
        className="w-full max-w-2xl bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <Compass className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">
              {isEditing ? "Editar Registro do Irmão" : "Cadastrar Novo Irmão"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-slate-800 bg-[#080C14]/50 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`member-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-b-2 transition-all whitespace-nowrap ${
                  active ? "text-[#D4AF37] border-[#D4AF37]" : "text-slate-400 border-transparent hover:text-white"
                }`}
              >
                <Icon className="w-3 h-3" />
                {t.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 text-xs text-slate-300 flex-1">
          {/* TAB: Dados */}
          {tab === "dados" && (
            <>
              {/* Foto do Irmão */}
              <div className="flex items-center gap-4 pb-3 border-b border-slate-800">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#080C14] border-2 border-[#D4AF37]/40 flex items-center justify-center shrink-0">
                  {form.photo_b64 ? (
                    <img src={form.photo_b64} alt="Foto do Irmão" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl text-slate-600 font-serif">
                      {(form.name || "?").trim().charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <label className={lbl}>Foto do Irmão (JPG/PNG, máx. 500 KB)</label>
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] text-[11px] font-bold uppercase tracking-wider transition-all">
                      <input
                        data-testid="input-member-photo"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 512 * 1024) {
                            alert("Foto muito grande. Máximo 500 KB.");
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (ev) => setField({ photo_b64: ev.target.result });
                          reader.readAsDataURL(f);
                        }}
                      />
                      Escolher foto
                    </label>
                    {form.photo_b64 && (
                      <button
                        type="button"
                        onClick={() => setField({ photo_b64: null })}
                        className="px-3 py-2 rounded-xl border border-slate-700 hover:border-rose-500 text-slate-400 hover:text-rose-400 text-[11px] font-bold uppercase transition-all"
                      >
                        Remover
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className={lbl}>Nome Completo *</label>
                <input data-testid="input-member-name" required value={form.name}
                  onChange={(e) => setField({ name: e.target.value })}
                  placeholder="Ex: Irmão Roberto Drummond" className={cls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>CIM *</label>
                  <input data-testid="input-member-cim" required value={form.cim}
                    onChange={(e) => setField({ cim: e.target.value })} className={`${cls} font-mono`} />
                </div>
                <div>
                  <label className={lbl}>Grau Simbólico</label>
                  <div
                    data-testid="display-member-degree"
                    className={`${cls} flex items-center justify-between cursor-not-allowed opacity-90`}
                    title="Definido automaticamente pelas datas ritualísticas"
                  >
                    <span className="font-serif font-bold text-[#D4AF37]">{form.degree}</span>
                    <span className="text-[9px] uppercase tracking-widest text-slate-500">Automático</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 italic">
                    Preencha as Datas Ritualísticas na próxima aba. O grau é atualizado automaticamente conforme as datas.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Cargo</label>
                  <select
                    data-testid="select-member-office"
                    value={form.office}
                    onChange={(e) => setField({ office: e.target.value })}
                    className={cls}
                  >
                    <option value="Membro do Quadro">Membro do Quadro</option>
                    <option disabled>─── Diretoria (Cargos Ativos) ───</option>
                    <option value="Venerável Mestre">Venerável Mestre</option>
                    <option value="1º Vigilante">1º Vigilante</option>
                    <option value="2º Vigilante">2º Vigilante</option>
                    <option value="Orador">Orador</option>
                    <option value="Orador Adjunto">Orador Adjunto</option>
                    <option value="Secretário">Secretário</option>
                    <option value="Secretário Adjunto">Secretário Adjunto</option>
                    <option value="Chanceler">Chanceler</option>
                    <option value="Tesoureiro">Tesoureiro</option>
                    <option value="Tesoureiro Adjunto">Tesoureiro Adjunto</option>
                    <option value="Hospitaleiro">Hospitaleiro</option>
                    <option value="Mestre de Cerimônias">Mestre de Cerimônias</option>
                    <option value="Mestre de Cerimônias Adjunto">Mestre de Cerimônias Adjunto</option>
                    <option value="Mestre de Harmonia">Mestre de Harmonia</option>
                    <option value="Mestre de Banquetes">Mestre de Banquetes</option>
                    <option value="Mestre Instalado (Past-Venerável)">Mestre Instalado (Past-Venerável)</option>
                    <option value="Deputado Federal">Deputado Federal</option>
                    <option value="Deputado Estadual">Deputado Estadual</option>
                    <option value="Deputado da Loja">Deputado da Loja</option>
                    <option value="Grande Deputado">Grande Deputado</option>
                    <option disabled>─── Guardas & Auxiliares ───</option>
                    <option value="1º Diácono">1º Diácono</option>
                    <option value="2º Diácono">2º Diácono</option>
                    <option value="1º Experto">1º Experto</option>
                    <option value="2º Experto">2º Experto</option>
                    <option value="Cobridor Interno">Cobridor Interno</option>
                    <option value="Cobridor Externo">Cobridor Externo</option>
                    <option value="Porta-Bandeira">Porta-Bandeira</option>
                    <option value="Porta-Estandarte">Porta-Estandarte</option>
                    <option value="Bibliotecário">Bibliotecário</option>
                    <option value="Arquivista">Arquivista</option>
                    <option value="Webmaster / TI">Webmaster / TI</option>
                    <option disabled>─── Situações Especiais ───</option>
                    <option value="Afastado">Afastado</option>
                    <option value="Remido">Remido</option>
                    <option value="Sono Maçônico">Sono Maçônico</option>
                    <option value="Visitante">Visitante</option>
                  </select>
                </div>
                <div>
                  <label className={lbl}>Aniversário</label>
                  <input data-testid="input-member-birthday" type="date" value={form.birthday}
                    onChange={(e) => setField({ birthday: e.target.value })} className={cls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>E-mail</label>
                  <input type="email" value={form.email} onChange={(e) => setField({ email: e.target.value })} className={cls} />
                </div>
                <div>
                  <label className={lbl}>Telefone</label>
                  <input value={form.phone} onChange={(e) => setField({ phone: e.target.value })} className={`${cls} font-mono`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Mensalidade (R$)</label>
                  <input type="number" step="0.01" value={form.monthly_fee}
                    onChange={(e) => setField({ monthly_fee: e.target.value })} className={`${cls} font-mono`} />
                </div>
                <div>
                  <label className={lbl}>Status</label>
                  <select value={form.status} onChange={(e) => setField({ status: e.target.value })} className={cls}>
                    <option value="regular">Regular</option>
                    <option value="irregular">Irregular</option>
                    <option value="afastado">Afastado</option>
                    <option value="remido">Remido</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={lbl}>Observações</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setField({ notes: e.target.value })} className={cls} />
              </div>

              <div className="pt-3 mt-2 border-t border-slate-800">
                <div className="mb-3 p-3 rounded-lg bg-[#080C14] border border-[#D4AF37]/40">
                  <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold flex items-center gap-2">
                    <Compass className="w-3 h-3" />
                    Login de Acesso do Irmão (CIM)
                  </label>
                  <input
                    data-testid="input-member-login"
                    type="text"
                    value={form.cim || ""}
                    onChange={(e) => setField({ cim: e.target.value })}
                    placeholder="Ex.: 62188"
                    className="w-full bg-transparent text-[#D4AF37] font-mono text-sm mt-1 p-1 border-0 outline-none focus:ring-0"
                    autoComplete="username"
                  />
                  <div className="text-[10px] text-slate-500 mt-1 italic">
                    Sincronizado com o campo CIM acima. O Irmão fará login usando o CIM + senha.
                  </div>
                </div>

                <label className={lbl}>
                  {isEditing ? "Redefinir Senha" : "Senha Inicial"}
                </label>
                <div className="relative">
                  <input
                    data-testid="input-member-password"
                    type={showPassword ? "text" : "password"}
                    value={form.password || ""}
                    onChange={(e) => setField({ password: e.target.value })}
                    placeholder={isEditing ? "Deixe em branco para manter a senha atual" : "Padrão: irmao123 (se em branco)"}
                    className={`${cls} pr-20`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    data-testid="btn-toggle-password-visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase font-bold text-[#D4AF37] hover:text-white px-2 py-1"
                  >
                    {showPassword ? "Ocultar" : "Mostrar"}
                  </button>
                </div>

                <label className={`${lbl} mt-2`}>Confirmar Senha</label>
                <input
                  data-testid="input-member-password-confirm"
                  type={showPassword ? "text" : "password"}
                  value={form.password_confirm || ""}
                  onChange={(e) => setField({ password_confirm: e.target.value })}
                  placeholder="Digite a senha novamente para confirmar"
                  className={cls}
                  autoComplete="new-password"
                />
                {form.password && form.password_confirm && form.password !== form.password_confirm && (
                  <div className="text-[10px] text-rose-400 mt-1 font-semibold" data-testid="password-mismatch-warning">
                    ⚠ As senhas não coincidem.
                  </div>
                )}
                {form.password && form.password_confirm && form.password === form.password_confirm && (
                  <div className="text-[10px] text-emerald-400 mt-1 font-semibold">
                    ✓ Senhas conferem.
                  </div>
                )}
                <div className="text-[10px] text-slate-500 mt-2 italic">
                  {isEditing
                    ? "Ao preencher e confirmar aqui, a senha do Irmão será substituída pela nova. O e-mail acima continua sendo o login."
                    : "Se deixado em branco, o Irmão receberá a senha padrão 'irmao123' e deve trocá-la no primeiro acesso."}
                </div>
              </div>

              <div className="pt-3 mt-2 border-t border-slate-800">
                <label className={lbl}>Título Honorífico (desconto vitalício)</label>
                <select
                  data-testid="select-member-honor-title"
                  value={form.honor_title || ""}
                  onChange={(e) => setField({ honor_title: e.target.value, honor_title_granted_at: e.target.value ? form.honor_title_granted_at : "" })}
                  className={cls}
                >
                  <option value="">— Nenhum —</option>
                  <option value="emerito">Maçom Emérito</option>
                  <option value="provecto">Maçom Provecto</option>
                </select>
                <div className="text-[10px] text-slate-500 mt-1 italic">
                  O valor do desconto vitalício é configurado em Tesouraria → Editar Regras. Aplicado automaticamente na capitação mensal.
                </div>
                {form.honor_title && (
                  <div className="mt-2">
                    <label className={lbl}>Título concedido em *</label>
                    <input
                      data-testid="input-member-honor-granted-at"
                      type="date"
                      value={form.honor_title_granted_at || ""}
                      onChange={(e) => setField({ honor_title_granted_at: e.target.value })}
                      className={cls}
                    />
                    <div className="text-[10px] text-slate-500 mt-1 italic">
                      O desconto só será aplicado em mensalidades com vencimento igual ou posterior a esta data. Mensalidades anteriores (mesmo em aberto/inadimplente) permanecem com o valor cheio.
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* TAB: Família */}
          {tab === "familia" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Nome da Esposa</label>
                  <input data-testid="input-member-spouse-name" value={form.spouse_name}
                    onChange={(e) => setField({ spouse_name: e.target.value })} className={cls} />
                </div>
                <div>
                  <label className={lbl}>Aniversário da Esposa</label>
                  <input data-testid="input-member-spouse-birthday" type="date" value={form.spouse_birthday}
                    onChange={(e) => setField({ spouse_birthday: e.target.value })} className={cls} />
                </div>
              </div>
              <div>
                <label className={lbl}>Data do Casamento</label>
                <input data-testid="input-member-wedding-date" type="date" value={form.wedding_date}
                  onChange={(e) => setField({ wedding_date: e.target.value })} className={cls} />
              </div>

              <div className="pt-4 border-t border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Filhos</div>
                  <button type="button" data-testid="member-btn-add-child" onClick={addChild}
                    className="text-[#D4AF37] hover:text-white flex items-center gap-1 text-[10px] uppercase font-bold">
                    <Plus className="w-3 h-3" /> Adicionar filho
                  </button>
                </div>
                <div className="space-y-2">
                  {form.children.map((c, i) => (
                    <div key={i} className="grid grid-cols-[1fr_140px_100px_auto] gap-2">
                      <input placeholder="Nome" value={c.name}
                        onChange={(e) => updChild(i, { name: e.target.value })} className={cls} />
                      <input type="date" value={c.birthday}
                        onChange={(e) => updChild(i, { birthday: e.target.value })} className={cls} />
                      <input placeholder="Relação" value={c.relation}
                        onChange={(e) => updChild(i, { relation: e.target.value })} className={cls} />
                      <button type="button" onClick={() => rmChild(i)}
                        className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/30 text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {form.children.length === 0 && <div className="text-slate-500 italic text-[10px]">Nenhum filho cadastrado.</div>}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Netos</div>
                  <button type="button" data-testid="member-btn-add-grand" onClick={addGrand}
                    className="text-[#D4AF37] hover:text-white flex items-center gap-1 text-[10px] uppercase font-bold">
                    <Plus className="w-3 h-3" /> Adicionar neto
                  </button>
                </div>
                <div className="space-y-2">
                  {form.grandchildren.map((c, i) => (
                    <div key={i} className="grid grid-cols-[1fr_140px_100px_auto] gap-2">
                      <input placeholder="Nome" value={c.name}
                        onChange={(e) => updGrand(i, { name: e.target.value })} className={cls} />
                      <input type="date" value={c.birthday}
                        onChange={(e) => updGrand(i, { birthday: e.target.value })} className={cls} />
                      <input placeholder="Relação" value={c.relation}
                        onChange={(e) => updGrand(i, { relation: e.target.value })} className={cls} />
                      <button type="button" onClick={() => rmGrand(i)}
                        className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/30 text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {form.grandchildren.length === 0 && <div className="text-slate-500 italic text-[10px]">Nenhum neto cadastrado.</div>}
                </div>
              </div>
            </>
          )}

          {/* TAB: Datas ritualísticas */}
          {tab === "ritual" && (
            <>
              <div className="p-3 rounded-lg bg-[#080C14] border border-[#D4AF37]/30 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1">Grau Simbólico Atual</div>
                <div className="text-sm text-white font-serif font-bold">{form.degree}</div>
                <div className="text-[10px] text-slate-500 mt-1 italic">Atualizado automaticamente pela data ritualística mais recente preenchida abaixo. Não editável manualmente.</div>
              </div>
              <div>
                <label className={lbl}>Data de Iniciação</label>
                <input type="date" value={form.initiation_date}
                  onChange={(e) => setField({ initiation_date: e.target.value })} className={cls} />
                <div className="text-[10px] text-slate-500 mt-1 italic">Data em que se tornou Aprendiz.</div>
              </div>
              <div>
                <label className={lbl}>Data de Elevação</label>
                <input type="date" value={form.elevation_date}
                  onChange={(e) => setField({ elevation_date: e.target.value })} className={cls} />
                <div className="text-[10px] text-slate-500 mt-1 italic">Data em que se tornou Companheiro.</div>
              </div>
              <div>
                <label className={lbl}>Data de Exaltação</label>
                <input type="date" value={form.exaltation_date}
                  onChange={(e) => setField({ exaltation_date: e.target.value })} className={cls} />
                <div className="text-[10px] text-slate-500 mt-1 italic">Data em que se tornou Mestre.</div>
              </div>
              <div>
                <label className={lbl}>Data de Instalação</label>
                <input
                  data-testid="input-member-installation-date"
                  type="date"
                  value={form.installation_date}
                  onChange={(e) => setField({ installation_date: e.target.value })}
                  className={cls}
                />
                <div className="text-[10px] text-slate-500 mt-1 italic">Data em que se tornou Mestre Instalado (Venerável Mestre já instalado ao menos uma vez).</div>
              </div>

              <div className="col-span-2 pt-4 mt-3 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-3">
                  Títulos Honoríficos (registro histórico)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lbl}>Data de Emérito</label>
                    <input
                      data-testid="input-member-emerito-date"
                      type="date"
                      value={form.emerito_date}
                      onChange={(e) => setField({ emerito_date: e.target.value })}
                      className={cls}
                    />
                    <div className="text-[10px] text-slate-500 mt-1 italic">
                      Data efetiva em que o Irmão foi elevado a Emérito. Fica registrado no histórico.
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Data de Provecto</label>
                    <input
                      data-testid="input-member-provecto-date"
                      type="date"
                      value={form.provecto_date}
                      onChange={(e) => setField({ provecto_date: e.target.value })}
                      className={cls}
                    />
                    <div className="text-[10px] text-slate-500 mt-1 italic">
                      Data efetiva em que o Irmão foi elevado a Provecto.
                    </div>
                  </div>
                </div>

                {form.initiation_date && (() => {
                  const initDate = new Date(form.initiation_date + "T00:00");
                  const today = new Date();
                  const yearsSinceInit = (today - initDate) / (365.25 * 24 * 60 * 60 * 1000);
                  const provectoTarget = new Date(initDate);
                  provectoTarget.setFullYear(provectoTarget.getFullYear() + 25); // Provecto ~ 25 anos
                  const diffMs = provectoTarget - today;
                  const diffYears = diffMs / (365.25 * 24 * 60 * 60 * 1000);
                  if (form.provecto_date) return null; // já é Provecto
                  if (yearsSinceInit >= 20 && diffYears > 0 && diffYears <= 5) {
                    const years = Math.floor(diffYears);
                    const months = Math.floor((diffYears - years) * 12);
                    return (
                      <div className="mt-4 p-3 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-yellow-500/5">
                        <div className="text-sm font-bold text-amber-300 mb-1">
                          ⏳ Faltam {years} ano{years !== 1 ? "s" : ""} {months > 0 ? `e ${months} ${months === 1 ? "mês" : "meses"}` : ""} para Provecto
                        </div>
                        <div className="text-[11px] text-slate-300">
                          O Irmão completou 20 anos de iniciação. Fica atento à frequência em sessões (mínimo 50%) e preparação ritualística.
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div className="pt-4 mt-3 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold mb-3">Desligamento & Retorno (opcional)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={lbl}>Pedido de Desligamento</label>
                    <input
                      data-testid="input-member-cancellation-date"
                      type="date"
                      value={form.cancellation_request_date}
                      onChange={(e) => setField({ cancellation_request_date: e.target.value })}
                      className={cls}
                    />
                    <div className="text-[10px] text-slate-500 mt-1 italic">Data em que o Irmão pediu desligamento.</div>
                  </div>
                  <div>
                    <label className={lbl}>Retorno / Regularização</label>
                    <input
                      data-testid="input-member-regularization-date"
                      type="date"
                      value={form.regularization_date}
                      onChange={(e) => setField({ regularization_date: e.target.value })}
                      className={cls}
                    />
                    <div className="text-[10px] text-slate-500 mt-1 italic">Data em que o Irmão retornou. Ajuste o grau acima manualmente se necessário.</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* TAB: Graus 4-33 */}
          {tab === "graus" && (
            <>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Graus filosóficos</div>
                <button type="button" data-testid="member-btn-add-degree" onClick={addDegree}
                  className="text-[#D4AF37] hover:text-white flex items-center gap-1 text-[10px] uppercase font-bold">
                  <Plus className="w-3 h-3" /> Adicionar grau
                </button>
              </div>
              <div className="text-[10px] text-slate-500 italic mb-2">
                Registre os graus filosóficos (Rito Escocês Antigo e Aceito ou outros) do 4 ao 33.
              </div>
              <div className="space-y-2">
                {form.higher_degrees.map((d, i) => (
                  <div key={i} className="grid grid-cols-[80px_140px_1fr_auto] gap-2">
                    <input type="number" min="4" max="33" value={d.degree}
                      onChange={(e) => updDegree(i, { degree: parseInt(e.target.value) || 4 })} className={`${cls} font-mono text-center`} />
                    <input type="date" value={d.date}
                      onChange={(e) => updDegree(i, { date: e.target.value })} className={cls} />
                    <input placeholder="Observação (opcional)" value={d.obs}
                      onChange={(e) => updDegree(i, { obs: e.target.value })} className={cls} />
                    <button type="button" onClick={() => rmDegree(i)}
                      className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/30 text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {form.higher_degrees.length === 0 && <div className="text-slate-500 italic text-[10px]">Nenhum grau cadastrado.</div>}
              </div>
            </>
          )}
        </form>

        <div className="px-6 py-4 border-t border-slate-800 bg-[#080C14] flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs">
            Cancelar
          </button>
          <button data-testid="btn-submit-member" onClick={handleSubmit}
            className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(212,175,55,0.25)]">
            {isEditing ? "Salvar Alterações" : "Cadastrar Irmão"}
          </button>
        </div>
      </div>
    </div>
  );
};
